import axios from 'axios';

import { debugMode } from '@/config/config.js';
import { getEnv, getEnvNumber } from '@/config/runtimeEnv';

const API_URL = getEnv('VUE_APP_BACKEND_URL', '/api/');
//este config local sobrepõe o config global, mas é mais fácil de gerir e manter do que passar por variáveis de ambiente (que exigem restart do FE para aplicar)
const ARRIVAL_TIMES_TIMEOUT_MS = getEnvNumber('VUE_APP_ARRIVAL_TIMES_TIMEOUT_MS', 12_000);
const ARRIVAL_TIMES_RETRY_DELAY_MS = getEnvNumber('VUE_APP_ARRIVAL_TIMES_RETRY_DELAY_MS', 500);
const ARRIVAL_TIMES_MAX_RETRIES = getEnvNumber('VUE_APP_ARRIVAL_TIMES_MAX_RETRIES', 1);

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // Tempo limite de 60 segundos para as requisições
});

export async function getArrivalTimes(busStopNumber, painelId) {
  if (debugMode) console.log(`busstop/${busStopNumber}`);

  for (let attempt = 0; attempt <= ARRIVAL_TIMES_MAX_RETRIES; attempt++) {
    try {
       if (debugMode) console.log('[API] Tentativa', attempt, '- a chamar api.get');

      const response = await api.get(`busstop/${busStopNumber}`, {
        params: painelId ? { painelId } : undefined,
        timeout: ARRIVAL_TIMES_TIMEOUT_MS,
      });

      if (debugMode) console.log('getArrivalTimes:', response.data);
      return response.data;
    } catch (error) {
      const isLastAttempt = attempt === ARRIVAL_TIMES_MAX_RETRIES;

      if (isLastAttempt || !isRetryableArrivalTimesError(error)) {
        console.error('Erro ao obter tempos de chegada:', error);
        throw error;
      }

      console.warn(
        `[getArrivalTimes] Tentativa ${attempt + 1} falhou para a paragem ${busStopNumber}: ${
          error.message
        }. A tentar novamente em ${ARRIVAL_TIMES_RETRY_DELAY_MS}ms.`
      );

      await new Promise((r) => setTimeout(r, ARRIVAL_TIMES_RETRY_DELAY_MS));
    }
  }
}

export async function getBusPositions(busStopNumber, painelId) {
  try {
    if (debugMode) console.log(`busstop/${busStopNumber}/buspositions`);
    const response = await api.get(`busstop/${busStopNumber}/buspositions`, {
      params: painelId ? { painelId } : undefined,
    });
    if (debugMode) console.log('getBusPositions:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter as posições dos autocarros:', error);
    throw error;
  }
}

export async function getBusStopConfig(busStopNumber, painelId) {
  try {
    if (debugMode) console.log(`busstop/${busStopNumber}/config`);
    const response = await api.get(`busstop/${busStopNumber}/config`, {
      params: painelId ? { painelId } : undefined,
    });
    if (debugMode) console.log('getBusStopConfig:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter a configurações do painel:', error);
    throw error;
  }
}

export async function getBusStopInfo(busStopNumber, painelId) {
  try {
    if (debugMode) console.log(`busstop/${busStopNumber}/info`);
    const response = await api.get(`busstop/${busStopNumber}/info`, {
      params: painelId ? { painelId } : undefined,
    });
    if (debugMode) console.log('getBusStopInfo:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter a Informação da Paragem:', error);
    throw error;
  }
}

export async function getAlerts(busStopNumber, painelId) {
  try {
    if (debugMode) console.log(`busstop/${busStopNumber}/alerts`);
    const response = await api.get(`busstop/${busStopNumber}/alerts`, {
      params: painelId ? { painelId } : undefined,
    });
    if (debugMode) console.log('getAlerts:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter dos alertas:', error);
    throw error;
  }
}

export async function getWeather(busStopNumber, painelId) {
  try {
    if (debugMode) console.log(`busstop/${busStopNumber}/weather`);
    const response = await api.get(`busstop/${busStopNumber}/weather`, {
      params: painelId ? { painelId } : undefined,
    });
    if (debugMode) console.log('getWeather:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter do clima:', error);
    throw error;
  }
}

// --- Endpoints dos painéis ---

// Faz refresh dos dados de um painel sem recarregar a página
export async function triggerPainelRefresh(painelId) {
  try {
    if (debugMode) console.log(`painel/${painelId}/refresh`);
    const response = await api.post(`painel/${painelId}/refresh`);
    if (debugMode) console.log('triggerPainelRefresh:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao disparar o refresh do painel:', error);
    throw error;
  }
}

// Força o reload completo da página de um painel
export async function triggerPainelReload(painelId) {
  try {
    if (debugMode) console.log(`paineis/${painelId}/reload`);
    const response = await api.post(`paineis/${painelId}/reload`);
    if (debugMode) console.log('triggerPainelReload:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao disparar o reload do painel:', error);
    throw error;
  }
}

// Verifica se um painel está activo/online
export async function checkPainelIsActive(painelId) {
  try {
    if (debugMode) console.log(`paineis/${painelId}/isActive`);
    const response = await api.post(`paineis/${painelId}/isActive`);
    if (debugMode) console.log('checkPainelIsActive:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar estado do painel:', error);
    throw error;
  }
}

function isRetryableArrivalTimesError(error) {
  // Retry: timeout do FE, erro de rede sem resposta, ou 5xx do BE
  // Não-retry: 4xx (incluindo 404 e auth) — são definitivos
  return error.code === 'ECONNABORTED' || !error.response || (error.response.status >= 500 && error.response.status < 600);
}
